/**
 * Brand logos: limits, sniffing and the lettermark fallback. Client-safe (no
 * Node imports) so the strip and the upload route share one definition.
 */

export const LOGO_MAX_BYTES = 256 * 1024

export const LOGO_MIMES = ['image/png', 'image/svg+xml', 'image/webp', 'image/jpeg'] as const
export type LogoMime = (typeof LOGO_MIMES)[number]

/** `accept` for the hidden file input. */
export const LOGO_ACCEPT = 'image/png,image/svg+xml,image/webp,image/jpeg,.png,.svg,.webp,.jpg,.jpeg'

const EXTENSIONS: Record<LogoMime, string> = {
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
}

export function isLogoMime(mime: string): mime is LogoMime {
  return (LOGO_MIMES as readonly string[]).includes(mime)
}

export function logoExtension(mime: string): string | null {
  return isLogoMime(mime) ? EXTENSIONS[mime] : null
}

/**
 * "3f9a1c2b7d4e.svg" — the file segment of the logo URL. The hash prefix makes
 * the URL immutable (a new upload gets a new URL) and the extension lets the
 * session proxy treat it like any other image.
 */
export function logoFileName(hash: string, mime: string): string | null {
  const extension = logoExtension(mime)
  if (!extension || hash.length < 12) return null
  return `${hash.slice(0, 12)}.${extension}`
}

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0) =>
  signature.every((byte, index) => bytes[offset + index] === byte)

/** The image type from the bytes themselves; the declared type is never trusted. */
export function sniffLogo(bytes: Uint8Array): LogoMime | null {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png'
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return 'image/jpeg'
  if (startsWith(bytes, [0x52, 0x49, 0x46, 0x46]) && startsWith(bytes, [0x57, 0x45, 0x42, 0x50], 8)) {
    return 'image/webp'
  }
  const text = decodeUtf8(bytes)
  if (text === null) return null
  const head = text.replace(/^\uFEFF/, '').trimStart().slice(0, 256).toLowerCase()
  if ((head.startsWith('<?xml') || head.startsWith('<!doctype') || head.startsWith('<svg')) && text.toLowerCase().includes('<svg')) {
    return 'image/svg+xml'
  }
  return null
}

function decodeUtf8(bytes: Uint8Array): string | null {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return null
  }
}

const UNSAFE_SVG: RegExp[] = [
  /<script/i,
  /<foreignobject/i,
  /<iframe|<embed|<object|<frame|<meta|<link/i,
  /<!entity/i,
  /\son[a-z]+\s*=/i,
  /javascript:/i,
  /\bhref\s*=\s*["'](?!#|data:image\/)/i,
  /xlink:href\s*=\s*["'](?!#|data:image\/)/i,
  /url\(\s*["']?(?!#|data:image\/)/i,
  /@import/i,
]

/**
 * Logos are drawn by <img>, which never runs scripts, and served with a CSP
 * sandbox for direct navigation. This is the third layer: an SVG that carries
 * scripting, handlers, external references or entities is refused outright.
 */
export function svgLooksSafe(text: string): boolean {
  return !UNSAFE_SVG.some((pattern) => pattern.test(text))
}

/** "Blackmagic Design" → "BD", "AJA" → "AJ", "Bluefish444" → "BL". */
export function brandInitials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (words.length === 0) return '?'
  const [first = '', second = ''] = words
  if (words.length >= 2) return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase()
  return first.slice(0, 2).toUpperCase()
}

/**
 * Deterministic pastel per brand so a lettermark keeps its colour across
 * visits. Each side is a light-dark() pair, so the tint deepens with the theme.
 */
export function brandTint(name: string): { background: string; color: string } {
  let hash = 0
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  const hue = hash % 360
  const next = (hue + 40) % 360
  return {
    background: `linear-gradient(135deg, light-dark(hsl(${hue} 85% 92%), hsl(${hue} 40% 22%)), light-dark(hsl(${next} 80% 88%), hsl(${next} 38% 18%)))`,
    color: `light-dark(hsl(${hue} 40% 34%), hsl(${hue} 60% 78%))`,
  }
}
